import {
  MIN_CAMPAIGN_DAYS,
  PRIZE_POOL_HIGH_RATE,
  PRIZE_POOL_LOW_RATE,
  chainsAreRequired,
} from "@/constants/selfRegisterCampaigns";
import type { SelfRegisterFormData } from "@/store/selfRegisterFormStore";
import { z } from "zod";

export type SelfRegisterFormErrors = Partial<Record<keyof SelfRegisterFormData, string>>;
export type SelfRegisterFormStep = "contact" | "campaign" | "expectations";

export const SELF_REGISTER_FORM_STEP_FIELDS: Record<SelfRegisterFormStep, (keyof SelfRegisterFormData)[]> = {
  contact: ["companyName", "telegramHandle", "email"],
  campaign: ["projectName", "exchanges", "chains", "tokenPair"],
  expectations: [
    "expectedDailyVolume",
    "campaignStartDate",
    "campaignEndDate",
    "discoverySource",
    "discoverySourceOther",
    "note",
    "fundingAcknowledged",
  ],
};

const parseNumber = (value: string): number | null => {
  const sanitized = value.replace(/[$,\s]/g, "");
  const parsed = Number.parseFloat(sanitized);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
};

/**
 * Calculate the duration in days between two dates.
 * Uses exclusive end date convention: Jan 1 to Jan 8 = 7 days.
 * This matches standard date range conventions (like hotel bookings).
 */
export const calculateDurationDays = (startDate?: string, endDate?: string): number | null => {
  if (!startDate || !endDate) return null;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const diffMs = end.getTime() - start.getTime();
  if (diffMs < 0) return null;
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
};

export const calculatePrizePoolRange = (data: SelfRegisterFormData) => {
  const expectedDailyVolume = parseNumber(data.expectedDailyVolume);
  const durationDays = calculateDurationDays(data.campaignStartDate, data.campaignEndDate);
  if (!expectedDailyVolume || !durationDays) return null;

  const totalVolume = expectedDailyVolume * durationDays;
  const low = totalVolume * PRIZE_POOL_LOW_RATE;
  const high = totalVolume * PRIZE_POOL_HIGH_RATE;
  const lowDaily = expectedDailyVolume * PRIZE_POOL_LOW_RATE;
  const highDaily = expectedDailyVolume * PRIZE_POOL_HIGH_RATE;

  return {
    durationDays,
    totalVolume,
    low,
    high,
    lowDaily,
    highDaily,
  };
};

const selfRegisterFormSchema = z
  .object({
    companyName: z.string().min(1, "Company name is required"),
    telegramHandle: z.string().min(1, "Telegram handle is required").max(64, "Telegram handle is too long"),
    email: z.email("Enter a valid email"),
    projectName: z.string().min(1, "Project name is required"),
    exchanges: z.array(z.string()).min(1, "Select at least one exchange"),
    chains: z.array(z.string()),
    tokenPair: z.string().min(1, "Token pair is required"),
    discoverySource: z.string().min(1, "Discovery source is required"),
    discoverySourceOther: z.string().max(200, "Custom discovery source must be under 200 characters").optional(),
    expectedDailyVolume: z
      .string()
      .min(1, "Expected daily volume is required")
      .refine((value) => parseNumber(value) !== null, "Enter a valid volume amount"),
    campaignStartDate: z.string().min(1, "Start date is required"),
    campaignEndDate: z.string().min(1, "End date is required"),
    note: z.string().max(300, "Note must be under 300 characters").optional(),
    fundingAcknowledged: z.literal(true, {
      message: "Please acknowledge the prize pool funding notice",
    }),
    walletAddress: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    const durationDays = calculateDurationDays(data.campaignStartDate, data.campaignEndDate);
    if (durationDays === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["campaignEndDate"],
        message: "End date must be after start date",
      });
      return;
    }

    if (durationDays < MIN_CAMPAIGN_DAYS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["campaignEndDate"],
        message: `Campaign must be at least ${MIN_CAMPAIGN_DAYS} days`,
      });
    }

    if (data.discoverySource === "other" && !data.discoverySourceOther?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["discoverySourceOther"],
        message: "Please specify how you discovered Cede Hub",
      });
    }

    // Chains are required only when DEX is selected (optional for custom-only)
    if (chainsAreRequired(data.exchanges) && data.chains.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["chains"],
        message: "Select at least one blockchain for DEX trading",
      });
    }
  });

export const validateSelfRegisterForm = (
  data: SelfRegisterFormData,
): { success: boolean; errors: SelfRegisterFormErrors } => {
  const result = selfRegisterFormSchema.safeParse(data);
  if (result.success) {
    return { success: true, errors: {} };
  }

  const errors: SelfRegisterFormErrors = {};
  result.error.issues.forEach((issue) => {
    const field = issue.path[0] as keyof SelfRegisterFormData | undefined;
    if (field && !errors[field]) {
      errors[field] = issue.message;
    }
  });

  return { success: false, errors };
};

export const validateSelfRegisterFormStep = (
  data: SelfRegisterFormData,
  step: SelfRegisterFormStep,
): { success: boolean; errors: SelfRegisterFormErrors } => {
  const fullValidation = validateSelfRegisterForm(data);
  if (fullValidation.success) {
    return { success: true, errors: {} };
  }

  const allowedFields = new Set(SELF_REGISTER_FORM_STEP_FIELDS[step]);
  const filteredErrors: SelfRegisterFormErrors = {};

  Object.entries(fullValidation.errors).forEach(([field, message]) => {
    if (allowedFields.has(field as keyof SelfRegisterFormData) && message) {
      filteredErrors[field as keyof SelfRegisterFormData] = message;
    }
  });

  return { success: Object.keys(filteredErrors).length === 0, errors: filteredErrors };
};
